import {
  computed,
  type ComputedRef,
} from 'vue';

import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {useRoute} from 'vue-router';
import {Language} from '@/ts/types/component/localization.type';
import type {
  InterventionPath,
  SessionPath,
ContentPath} from '@/ts/router/pages-path';
import {LANGUAGE_REGEX} from '@/ts/utils/consts';
import {getCurrentUrlLanguage} from '@/ts/composables/stateful/use-i18n-router';
import {persistentStorage} from '@/ts/instances/persistant-storage';


interface PathParams {
  [InterventionPath.interventionInfo]: { iid: UuidDTO };
  [InterventionPath.interventionInfoAbout]: { iid: UuidDTO };
  [InterventionPath.interventionRecap]: { iid: UuidDTO };
  [InterventionPath.interventionInfoSessions]: { iid: UuidDTO };
  [InterventionPath.interventionLanding]: { iid: UuidDTO };
  [SessionPath.sessionPage]: { iid: UuidDTO, sid: UuidDTO, pid: number };
  [SessionPath.sessionCompletedPage]: { iid: UuidDTO, sid: UuidDTO };
  [ContentPath.recommendation]: {contentType: string, catalogId: string, itemId: string };
}

type RequiredParams<T> = T extends keyof PathParams ? PathParams[T] : never;

/** Generates a language-prefixed Vue Router path with interpolated route params */
export function getVueLink<Path extends keyof PathParams>(
  path: Path,
  args: RequiredParams<Path>,
  lang?: Language,
): string {
  // Priority: explicit lang > current URL lang > stored lang > default
  const targetLang = lang ?? getCurrentUrlLanguage() ?? persistentStorage.language ?? Language.English;

  let localizedPath = path as string;
  for (const [param, id] of Object.entries(args)) {
    localizedPath = localizedPath?.replace(`:${param}`, id as string) ?? localizedPath;
  }

  // Always add language prefix if not already present
  if (!LANGUAGE_REGEX.STARTS_WITH_LANG.exec(localizedPath)) {
    if (localizedPath === '/') {
      localizedPath = `/${targetLang}`;
    } else {
      localizedPath = `/${targetLang}${localizedPath}`;
    }
  }

  return localizedPath;
}

/** Extracts typed route params (intervention, session, page, content, language) from the current route */
export function useRouterSectionParams(): ({
  iid: ComputedRef<UuidDTO>; // id of the intervention
  sid: ComputedRef<UuidDTO>; // id of the intervention session
  pid: ComputedRef<number>; // id of the intervention session page
  catalogId: ComputedRef<string>; // id of the intervention session page
  itemId: ComputedRef<string>; // id of the intervention session page
  contentType: ComputedRef<string>; // id of the intervention session page
  lang: ComputedRef<string>; // language parameter from the route
}) {
  const route = useRoute();
  const iid = computed(() => route.params.iid as UuidDTO);
  const sid = computed(() => route.params.sid as UuidDTO);
  const pid = computed(() => route.params.pid ? parseInt(route.params.pid as string, 10): 0);
  const catalogId = computed(() => route.params.catalogId as string);
  const itemId = computed(() => route.params.itemId as string);
  const contentType = computed(() => route.params.contentType as string);
  const lang = computed(() => route.params.lang as string);
  return {iid, sid, pid, catalogId, itemId, contentType, lang};
}
